import axios from 'axios';
import stripePromise from '../../../stripe';
import imageCompression from 'browser-image-compression';
import { auth } from '../../Login/Firebase';

const hostname = window.location.hostname;

const isLocal =
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  /^192\.168\./.test(hostname);

const isTest =
  hostname === 'test.pawsible.app';

export const API_BASE_URL = isLocal
  ? 'http://localhost:6060'
  : isTest
    ? 'https://test.pawsible.app'
    : 'https://www.pawsible.app';

// User
export const fetchAllUsers = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/users`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch all users:', error);
    throw error;
  }
};

export const fetchUserData = async (uid) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/users/${uid}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user data:', error.response.data.message);
    throw error;
  }
};

export const fetchStaffMembers = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/users/staff`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch staff members:', error);
    throw error;
  }
};

export const fetchCustomers = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/users`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch customers:', error);
    throw error;
  }
};

export const setDefaultPaymentMethod = async (uid, paymentMethodId) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/users/${uid}/default-payment-method`,
      { paymentMethodId }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to set default payment method:', error);
    throw error;
  }
};

export const editMember = async (uid, userData) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/users/${uid}`, userData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to add family member:', error);
    throw error;
  }
};

export const updateProfileComplete = async (uid, profileCompleteStatus) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/users/${uid}/profileComplete`, {
      profileComplete: profileCompleteStatus
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update profileComplete status:', error);
    throw error;
  }
};

// Cumulative Financial Summary
export const fetchFinancialSummary = async () => {
  try {
      const response = await axios.get(`${API_BASE_URL}/api/finance/summary`);
      return response.data;
  } catch (error) {
      console.error('Failed to fetch total revenue:', error);
      throw error;
  }
};

// Frontend API function to update store location
export const updateStoreLocation = async (uid, storeLocation) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/users/${uid}/storeLocation`, {
      storeLocation
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update store location:', error);
    throw error;
  }
};

export const updateUserPhoneNumber = async (uid, phoneNumber) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/users/${uid}/update-phone`, { phoneNumber }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update user phone number:', error);
    throw error;
  }
};

// Family Members
export const addFamilyMember = async (familyMemberData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/family-members`, familyMemberData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to add family member:', error);
    throw error;
  }
};

export const editFamilyMember = async (familyMemberId, familyMemberData) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/family-members/${familyMemberId}/update`, familyMemberData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update family member:', error);
    throw error;
  }
};

export const fetchAllFamilyMember = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/family-members`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch all users:', error);
    throw error;
  }
};

export const fetchFamilyMember = async (familyMemberId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/family-members/${familyMemberId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch family member details:', error);
    throw error;
  }
};

export const fetchUserFamilyMembers = async (userId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/family-members/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user family members:', error);
    throw error;
  }
};

export const deleteFamilyMember = async (familyMemberId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/api/family-members/${familyMemberId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete family member:', error);
    throw error;
  }
};

// Service
export const fetchService = async (serviceName) => {
  try {
      const response = await axios.get(`${API_BASE_URL}/api/service/${serviceName}`);
      return response.data;
  } catch (error) {
      console.error('Failed to fetch adventure details:', error);
      throw error;
  }
};

export const fetchAllServices = async () => {
  try {
      const response = await axios.get(`${API_BASE_URL}/api/service/getAllServices`);
      return response.data;
  } catch (error) {
      console.error('Failed to fetch adventure details:', error);
      throw error;
  }
};

// Bookings
export const createBooking = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/booking`,
      payload
    );
    return response.data; // { bookingId }
  } catch (error) {
    console.error('Failed to create booking:', error);
    throw error;
  }
};

export const createStaffBooking = async (payload) => {
  console.log(`getting here in API with payload:`, payload)
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/booking/staff`,
      payload
    );
    return response.data; // { bookingId }
  } catch (error) {
    console.error('Failed to create staff booking:', error);
    throw error;
  }
};

export const fetchBooking = async (id) => {
  try {
      const response = await axios.get(`${API_BASE_URL}/api/booking/${id}`);
      return response.data;
  } catch (error) {
      console.error('Failed to fetch booking details:', error);
      throw error;
  }
};

export const fetchBookings = async () => {
  try {
      const response = await axios.get(`${API_BASE_URL}/api/booking`);
      return response.data;
  } catch (error) {
      console.error('Failed to fetch bookings:', error);
      throw error;
  }
};

export const fetchGroomingBookings = async () => {
  try {
      const response = await axios.get(`${API_BASE_URL}/api/booking/grooming`);
      return response.data;
  } catch (error) {
      console.error('Failed to fetch grooming bookings:', error);
      throw error;
  }
};

export const assignBookingToStaff = async (bookingId, staffId) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/booking/${bookingId}/assign`, { assignedTo: staffId });
    return response.data;
  } catch (error) {
    console.error('Failed to assign booking to staff:', error);
    throw error;
  }
};

export const updateBookingNote = async (bookingId, note) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/booking/${bookingId}/note`, { specialNotes: note });
    return response.data;
  } catch (error) {
    console.error('Failed to update booking note:', error);
    throw error;
  }
};

export const fetchServiceAvailability = async (serviceType, date) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/booking/serviceAvailability`,
      {
        params: {
          serviceType,
          date
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Failed to fetch service availability:', error);
    throw error;
  }
};

export const fetchHotelAvailability = async (startDate, endDate) => {
  try {
      const response = await axios.get(`${API_BASE_URL}/api/booking/hotelAvailability`, {
        params: { startDate, endDate }
    });
      return response.data;
  } catch (error) {
      console.error('Failed to fetch availability:', error);
      throw error;
  }
};

export const getHotelAvailabilityByMonth = async (month, year) => {
  try {
      // Constructing the first day of the month and the last day of the month based on input month and year
      const startDate = new Date(year, month, 1); // This will give you the first day of the month
      const endDate = new Date(year, month + 1, 0); // This will give you the last day of the month

      const response = await axios.get(`${API_BASE_URL}/api/booking/hotelAvailability/${year}/${month}`, {
        params: { 
          startDate: startDate.toISOString().split('T')[0], // Format to YYYY-MM-DD
          endDate: endDate.toISOString().split('T')[0] // Format to YYYY-MM-DD
        }
      });

      return response.data;
  } catch (error) {
      console.error('Failed to fetch availability:', error);
      throw error;
  }
};

export const fetchDaycareAvailability = async (date) => {
  try {
      const response = await axios.get(`${API_BASE_URL}/api/booking/daycareAvailability`, {
        params: { date }
    });
      return response.data;
  } catch (error) {
      console.error('Failed to fetch availability:', error);
      throw error;
  }
};

export const getDaycareAvailabilityByMonth = async (month, year) => {
  try {
      // Constructing the first day of the month and the last day of the month based on input month and year
      const startDate = new Date(year, month, 1); // This will give you the first day of the month
      const endDate = new Date(year, month + 1, 0); // This will give you the last day of the month

      const response = await axios.get(`${API_BASE_URL}/api/booking/daycareAvailability/${year}/${month}`, {
        params: { 
          startDate: startDate.toISOString().split('T')[0], // Format to YYYY-MM-DD
          endDate: endDate.toISOString().split('T')[0] // Format to YYYY-MM-DD
        }
      });

      return response.data;
  } catch (error) {
      console.error('Failed to fetch availability:', error);
      throw error;
  }
};

export const getGroomingAvailabilityByMonth = async (month, year) => {
  try {
      // Constructing the first day of the month and the last day of the month based on input month and year
      const startDate = new Date(year, month, 1); // This will give you the first day of the month
      const endDate = new Date(year, month + 1, 0); // This will give you the last day of the month

      const response = await axios.get(`${API_BASE_URL}/api/booking/groomingAvailability/${year}/${month}`, {
        params: { 
          startDate: startDate.toISOString().split('T')[0], // Format to YYYY-MM-DD
          endDate: endDate.toISOString().split('T')[0] // Format to YYYY-MM-DD
        }
      });

      return response.data;
  } catch (error) {
      console.error('Failed to fetch availability:', error);
      throw error;
  }
};

export const getTrainingAvailabilityByMonth = async (month, year) => {
  try {
      // Constructing the first day of the month and the last day of the month based on input month and year
      const startDate = new Date(year, month, 1); // This will give you the first day of the month
      const endDate = new Date(year, month + 1, 0); // This will give you the last day of the month

      const response = await axios.get(`${API_BASE_URL}/api/booking/trainingAvailability/${year}/${month}`, {
        params: { 
          startDate: startDate.toISOString().split('T')[0], // Format to YYYY-MM-DD
          endDate: endDate.toISOString().split('T')[0] // Format to YYYY-MM-DD
        }
      });

      return response.data;
  } catch (error) {
      console.error('Failed to fetch availability:', error);
      throw error;
  }
};

export const getAdventureAvailabilityByMonth = async (month, year) => {
  try {
      // Constructing the first day of the month and the last day of the month based on input month and year
      const startDate = new Date(year, month, 1); // This will give you the first day of the month
      const endDate = new Date(year, month + 1, 0); // This will give you the last day of the month

      const response = await axios.get(`${API_BASE_URL}/api/booking/adventureAvailability/${year}/${month}`, {
        params: { 
          startDate: startDate.toISOString().split('T')[0], // Format to YYYY-MM-DD
          endDate: endDate.toISOString().split('T')[0] // Format to YYYY-MM-DD
        }
      });

      return response.data;
  } catch (error) {
      console.error('Failed to fetch availability:', error);
      throw error;
  }
};

export const getShuttleAvailabilityByMonth = async (month, year) => {
  try {
      // Constructing the first day of the month and the last day of the month based on input month and year
      const startDate = new Date(year, month, 1); // This will give you the first day of the month
      const endDate = new Date(year, month + 1, 0); // This will give you the last day of the month

      const response = await axios.get(`${API_BASE_URL}/api/booking/shuttleAvailability/${year}/${month}`, {
        params: { 
          startDate: startDate.toISOString().split('T')[0], // Format to YYYY-MM-DD
          endDate: endDate.toISOString().split('T')[0] // Format to YYYY-MM-DD
        }
      });

      return response.data;
  } catch (error) {
      console.error('Failed to fetch availability:', error);
      throw error;
  }
};

export const getTrainingTimeSlots = async (year, month, day, sessionType) => {
  try {
      const response = await axios.get(`${API_BASE_URL}/api/booking/training/timeSlots`, {
          params: { year, month, day, sessionType }
      });
      return response.data;
  } catch (error) {
      console.error('Error fetching training time slots:', error);
      throw error;
  }
};

// Service Configurations
export const fetchConfig = async (serviceName) => {
  try {
      const response = await axios.get(`${API_BASE_URL}/api/service-config/${serviceName}`);
      return response.data;
  } catch (error) {
      console.error('Failed to fetch configuration:', error.response ? error.response.data.message : error.message);
      throw error;
  }
};

export const updateConfig = async (serviceName, config) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/service-config/${serviceName}`, { serviceName, config });
    return response.data;
  } catch (error) {
    console.error('Failed to update configuration:', error.response ? error.response.data.message : error.message);
    throw error;
  }
}

// AWS S3
export async function uploadFileToS3(file) {
  try {
    let uploadFile = file;

    // ✅ Only compress if it's an image
    if (file.type.startsWith('image/')) {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };
      uploadFile = await imageCompression(file, options);
    }

    const formData = new FormData();
    formData.append('file', uploadFile);

    const response = await axios.post(`${API_BASE_URL}/api/s3/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data.url;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
}

// Google Maps
export const fetchDistanceAndDuration = async (pickup, dropoff, departureTime) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/maps/distance-duration`, {
      params: { pickup, dropoff, departureTime }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching distance and duration:', error.response ? error.response.data.message : error.message);
    throw error;
  }
};

export const fetchShuttleAvailabilityForAirport = async (pickup, dropoff, departureTime, departureDate) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/maps/calculate-airport-availability`, {
      params: { pickup, dropoff, departureTime, departureDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching shuttle calculation and availability:', error.response ? error.response.data.message : error.message);
    throw error;
  }
};

export const fetchShuttleAvailabilityForDaycare = async (pickup, dropoff, departureTime, departureDate) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/maps/calculate-daycare-availability`, {
      params: { pickup, dropoff, departureTime, departureDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching shuttle calculation and availability:', error.response ? error.response.data.message : error.message);
    throw error;
  }
};

export const fetchShuttleAvailabilityForHotel = async (pickup, dropoff, departureTime, departureDate) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/maps/calculate-hotel-availability`, {
      params: { pickup, dropoff, departureTime, departureDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching shuttle calculation and availability:', error.response ? error.response.data.message : error.message);
    throw error;
  }
};

export const fetchShuttleAvailabilityForTraining = async (pickup, dropoff, departureTime, departureDate) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/maps/calculate-training-availability`, {
      params: { pickup, dropoff, departureTime, departureDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching shuttle calculation and availability:', error.response ? error.response.data.message : error.message);
    throw error;
  }
};

export const fetchShuttleAvailabilityForGrooming = async (pickup, dropoff, departureTime, departureDate) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/maps/calculate-grooming-availability`, {
      params: { pickup, dropoff, departureTime, departureDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching shuttle calculation and availability:', error.response ? error.response.data.message : error.message);
    throw error;
  }
};

export const fetchShuttleAvailabilityForAdventure = async (pickup, dropoff, departureTime, departureDate) => {
  try {
    const response = await axios.get('${API_BASE_URL}/api/maps/calculate-adventure-availability', {
      params: { pickup, dropoff, departureTime, departureDate }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching shuttle calculation and availability:', error.response ? error.response.data.message : error.message);
    throw error;
  }
};

// Cart
export const addCartItem = async (bookingDetails) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/cart`, bookingDetails);
    return response.data;
  } catch (error) {
    console.error('Error adding cart item:', error.response ? error.response.data.message : error.message);
    throw error;
  }
};

export const removeItemFromCart = async (itemId) => {
  try {
      const response = await axios.delete(`${API_BASE_URL}/api/cart/${itemId}`);
      return response.data;
  } catch (error) {
      throw new Error('Failed to remove item from cart');
  }
};

// Payment
export const addPaymentMethod = async (userId, paymentDetails) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/payment/${userId}/payment-method`,
      paymentDetails
    );
    return response.data;
  } catch (error) {
    console.error(
      'Error adding payment method:',
      error.response ? error.response.data.message : error.message
    );
    throw error;
  }
};

export const getPaymentMethodById = async (paymentMethodId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/payment/${paymentMethodId}`
    );
    return response.data;
  } catch (error) {
    console.error(
      'Error getting payment method by ID:',
      error.response ? error.response.data.message : error.message
    );
    throw error;
  }
};

export const updatePaymentMethod = async (uid, updatedDetails) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/payment/${uid}/update-method`,
      updatedDetails
    );
    return response.data;
  } catch (error) {
    console.error(
      'Error updating payment method:',
      error.response ? error.response.data.message : error.message
    );
    throw error;
  }
};

export const deletePaymentMethod = async (paymentMethodId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/api/payment/${paymentMethodId}`
    );
    return response.data;
  } catch (error) {
    console.error(
      'Error deleting payment method:',
      error.response ? error.response.data.message : error.message
    );
    throw error;
  }
};

// Wallet
export async function updateWalletBalance(userId, newBalance) {
  const url = API_BASE_URL + '/api/wallet/' + userId + '/update';
  const body = { newBalance: newBalance };

  const u = auth.currentUser;
  if (!u) {
    throw new Error('Not signed in');
  }

  async function getToken(forceRefresh) {
    return u.getIdToken(!!forceRefresh);
  }

  try {
    const idToken = await getToken(false);
    const resp = await axios.put(url, body, {
      headers: { Authorization: 'Bearer ' + idToken },
      withCredentials: true
    });
    return resp.data;
  } catch (err) {
    let is401 = false;
    if (err && err.response && typeof err.response.status === 'number') {
      if (err.response.status === 401) {
        is401 = true;
      }
    }

    if (is401) {
      const fresh = await getToken(true);
      const resp2 = await axios.put(url, body, {
        headers: { Authorization: 'Bearer ' + fresh },
        withCredentials: true
      });
      return resp2.data;
    }

    throw err;
  }
}

// Stripe
export const processPayment = async (userId, amount, email, phone, defaultMethodId) => {
  try {
      const { data } = await axios.post(`${API_BASE_URL}/api/payment/${userId}/process-payment`, {
          amount,
          email,
          phone,
          defaultMethodId,
      });

      if (!userId) throw new Error('Failed to get user ID.');

      const clientSecret = data.clientSecret;
      if (!clientSecret) throw new Error('Failed to get client secret from server.');

      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe is not initialized');

      // Confirm the payment using the payment method ID
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: defaultMethodId, // Using the saved payment method ID
      });

    if (result.error) {
        throw new Error(result.error.message);
    } else if (result.paymentIntent.status === 'succeeded') {
        return result.paymentIntent;
    } else {
        throw new Error('Payment failed. Please try again.');
    }
  } catch (error) {
      console.error('Error processing payment:', error.response ? error.response.data.message : error.message);
      throw error;
  }
};

const api = axios.create({
  baseURL: API_BASE_URL
});

// frontend/api/index.js (where your interceptor lives)
api.interceptors.request.use(async (config) => {
  const current = auth.currentUser;
  if (!current) {
    // You must be signed in; otherwise there’s no token to send.
    // Show a login modal or block the action.
    throw new Error('Not signed in');
  }
  const idToken = await current.getIdToken(true);
  config.headers.Authorization = `Bearer ${idToken}`;
  return config;
});

export async function processStripePayment(
  stripeCustomerId,
  paymentMethodId,
  cart,
  finalTotalRender,      // UI display only
  currency,
  description,
  captureMethod,
  user,
  extra = {}
) {
  return api
    .post(`/api/payment/${stripeCustomerId}/process-stripe-payment`, {
      // required
      paymentMethodId,
      currency,
      description,
      captureMethod,
      cart,
      user,

      // UI reference only (backend logs this, does NOT trust it)
      finalTotalRender,

      // discounts & credits
      promoCode: extra.promoCode || null,
      cartLevelDiscountCents: extra.cartLevelDiscountCents || 0,
      bundleDiscountCents: extra.bundleDiscountCents || 0,

      // wallet
      useWallet: !!extra.useWallet,
      appliedWalletCents: extra.appliedWalletCents || 0,

      // service charges
      serviceChargeCents: extra.serviceChargeCents || 0
    })
    .then(res => res.data);
}

export const createPayment = async (userId, amount, email, phone, paymentMethodId) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/payment/${userId}/create-payment-method`, {
      amount,
      email,
      phone,
      paymentMethodId,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const createStripeCustomer = async (userId, email) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/payment/${userId}/create-stripe-customer`,
      { email }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to create Stripe customer:', error);
    throw error;
  }
};

export const attachPaymentMethodToCustomer = async (
  stripeCustomerId,
  paymentMethodId,
  userId
) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/payment/${stripeCustomerId}/attach-payment-method`,
      { paymentMethodId, userId }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to attach payment method:', error);
    throw error;
  }
};

export const capturePayment = async (paymentIntentId, amountToCapture) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/payment/capture`, {
      paymentIntentId,
      amountToCapture,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const cancelPayment = async (paymentIntentId) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/payment/cancel`, {
      paymentIntentId,
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// Payment History
export const fetchPaymentHistoryByUserId = async (userId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/booking/payment-history/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch payment history:', error);
    throw error;
  }
};

export const updateTransaction = async (bookingId, transaction) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/booking/payment-history/${bookingId}`, transaction);
    return response.data;
  } catch (error) {
    console.error('Failed to update transaction:', error);
    throw error;
  }
};

// Cancel Transaction
export const cancelTransaction = async (bookingId) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/booking/payment-history/${bookingId}`, {
      status: 'Canceled', // Update the status to "Canceled"
    });
    return response.data;
  } catch (error) {
    console.error('Failed to cancel transaction:', error);
    throw error;
  }
};

// Referral Redeem
export const redeemReferralCode = async (code, userCtx) => {
  const payload = {
    code: code,
    redeemer: {
      id:  userCtx && userCtx.id ? userCtx.id : null,     // Users.id (int)
      uid: userCtx && userCtx.uid ? String(userCtx.uid) : null, // Users.uid (string)
      email: userCtx && userCtx.email ? String(userCtx.email) : null
    }
  };

  try {
    const resp = await axios.post(`${API_BASE_URL}/api/users/redeem`, payload);
    if (resp && resp.data) return resp.data;
    return { success: true, message: 'OK' };
  } catch (err) {
    if (err && err.response && err.response.data) return err.response.data;
    return { success: false, message: 'Network error' };
  }
};

// Incident Logs
export const fetchIncidents = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/api/incidents`);
    return res.data;
  } catch (err) {
    console.error('Error fetching incidents:', err);
    throw err;
  }
};

export const createIncident = async (incidentData) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/api/incidents`, incidentData);
    return res.data;
  } catch (err) {
    console.error('Error creating incident:', err);
    throw err;
  }
};

export const updateIncident = async (incidentId, updateData) => {
  try {
    const res = await axios.put(`${API_BASE_URL}/api/incidents/${incidentId}`, updateData);
    return res.data;
  } catch (err) {
    console.error('Error updating incident:', err);
    throw err;
  }
};

// Shifts
export const fetchStaffShifts = async (userId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/staff-shifts/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch staff shifts:', error);
    throw error;
  }
};

export const createShift = async (shiftData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/staff-shifts`, shiftData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create shift:', error);
    throw error;
  }
};

export const updateShift = async (shiftId, updatedData) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/staff-shifts/${shiftId}`, updatedData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update shift:', error);
    throw error;
  }
};

// Announcements
export const fetchAnnouncements = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/announcements`);

    if (Array.isArray(response.data)) {
      return response.data;
    } else {
      return [];
    }
  } catch (error) {
    console.error('Failed to fetch announcements:', error);
    return [];
  }
};

// Memberships
export const fetchMembershipCharges = async (userId) => {
  try {
    const res = await axios.get(
      `${API_BASE_URL}/api/memberships/${userId}/charges`
    );
    return res.data;
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const fetchBundleHistory = async (userId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/bundles/${userId}/history`
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getMembershipById = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/memberships/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch membership by ID:', error);
    throw error;
  }
};

export const updateMembership = async (id, updatedMembershipData, stripePaymentMethodId, stripeCustomerId, paymentDifference) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/memberships/${id}`, updatedMembershipData, stripePaymentMethodId, stripeCustomerId, paymentDifference);
    return response.data;
  } catch (error) {
    console.error('Failed to update membership:', error);
    throw error;
  }
};

export const deleteMembership = async (id) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/api/memberships/${id}/delete`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete membership:', error);
    throw error;
  }
};

// Friends
export async function fetchSuggestedFriends(uid) {
  try {
    const resp = await axios.get(`${API_BASE_URL}/api/friends/suggested`, { params: { uid: uid } });
    return resp && resp.data && resp.data.data ? resp.data.data : [];
  } catch (err) {
    return [];
  }
}

export async function addFriend(payload) {
  // payload: { userUid, friendUid }
  try {
    const resp = await axios.post(`${API_BASE_URL}/api/friends`, payload);
    return resp && resp.data ? resp.data : { success: false };
  } catch (err) {
    return { success: false };
  }
}

// Twilio
export const sendVerificationCode = async (phoneNumber) => {
  try {
      const response = await axios.post(`${API_BASE_URL}/api/send-verification-code`, {
          phoneNumber,
      });
      if (response.data.success) {
          console.log('Verification code sent successfully.');
      } else {
          console.error(response.data.message);
      }
  } catch (error) {
      console.error('Error sending verification code:', error);
  }
};

export const verifyTwilioCode = async (phoneNumber, code) => {
  try {
      const response = await axios.post(`${API_BASE_URL}/api/verify-code`, {
          phoneNumber,
          code,
      });
      return response.data;
  } catch (error) {
      console.error('Error verifying code:', error);
      return { success: false, error: error.message };
  }
};

// --- Staff Payments (Reception) shims ---
// Re-exports and thin wrappers so StaffPaymentsReception works without touching other screens.

// Small helper to build query params without optional chaining
function pickQuery(opts) {
  const o = opts || {};
  const q = {};
  if (typeof o.status === 'string' && o.status) q.status = o.status;
  if (typeof o.method === 'string' && o.method) q.method = o.method;
  if (typeof o.from === 'string' && o.from) q.from = o.from;          // YYYY-MM-DD
  if (typeof o.to === 'string' && o.to) q.to = o.to;                   // YYYY-MM-DD
  if (o.last90 === true || o.last90 === 'true' || o.last90 === 1 || o.last90 === '1') q.last90 = '1';
  if (typeof o.limit === 'number') q.limit = o.limit;
  if (typeof o.offset === 'number') q.offset = o.offset;
  if (typeof o.q === 'string' && o.q) q.q = o.q;
  return q;
}

export async function fetchStaffPaymentsByUser(userId, opts) {
  const params = pickQuery(opts);
  const url = API_BASE_URL + '/api/booking/payment-history/' + encodeURIComponent(userId);
  const res = await axios.get(url, { params });
  // Expecting { rows: [], total: number }
  if (res && res.data && Array.isArray(res.data.rows)) return res.data;
  // Fallback to old shape if backend returns array
  if (res && Array.isArray(res.data)) return { rows: res.data, total: res.data.length };
  return { rows: [], total: 0 };
}

export async function searchStaffPayments(q, opts) {
  const params = pickQuery(opts);
  params.q = q;
  const url = API_BASE_URL + '/api/booking/payment-history/search';
  const res = await axios.get(url, { params });
  if (res && res.data && Array.isArray(res.data.rows)) return res.data;
  if (res && Array.isArray(res.data)) return { rows: res.data, total: res.data.length };
  return { rows: [], total: 0 };
}

// 2) Refund a settled charge
export async function refundPayment(paymentId, amount) {
  try {
    const payload = amount ? { amount } : {};
    const res = await axios.post(`${API_BASE_URL}/api/payment/${paymentId}/refund`, payload);
    return res.data;
  } catch (err) {
    console.error('Refund failed:', err);
    throw err;
  }
}

// 3) Void an authorization (pre-capture)
export async function voidPayment(paymentId) {
  try {
    const res = await axios.post(`${API_BASE_URL}/api/payment/${paymentId}/void`);
    return res.data;
  } catch (err) {
    console.error('Void failed:', err);
    throw err;
  }
}

// 4) Resend a receipt
export async function resendReceipt(paymentId) {
  try {
    const res = await axios.post(`${API_BASE_URL}/api/payment/${paymentId}/resend-receipt`);
    return res.data;
  } catch (err) {
    console.error('Resend receipt failed:', err);
    throw err;
  }
}

// 5) Print / fetch a receipt (PDF or HTML)
export async function printReceipt(paymentId) {
  try {
    const res = await axios.get(`${API_BASE_URL}/api/payment/${paymentId}/receipt`, {
      responseType: 'blob' // expect PDF
    });
    // Open in new tab
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
    return true;
  } catch (err) {
    console.error('Print receipt failed:', err);
    throw err;
  }
}

// --- Reception Check-In/Out shims ---

export async function checkInBooking(bookingId, payload) {
  try {
    // POST so we can carry metadata: at, staffId, notes, etc.
    const res = await axios.post(`${API_BASE_URL}/api/booking/${bookingId}/checkin`, payload || {});
    return res.data;
  } catch (err) {
    console.error('Check-in failed:', err);
    throw err;
  }
}

export async function checkOutBooking(bookingId, payload) {
  try {
    const res = await axios.post(`${API_BASE_URL}/api/booking/${bookingId}/checkout`, payload || {});
    return res.data;
  } catch (err) {
    console.error('Check-out failed:', err);
    throw err;
  }
}

export async function resendReceiptForBooking(bookingId) {
  try {
    const res = await axios.post(`${API_BASE_URL}/api/booking/${bookingId}/resend-receipt`);
    return res.data;
  } catch (err) {
    console.error('Resend booking receipt failed:', err);
    throw err;
  }
}

// ===== Memberships (Reception) =====

// list all active/paused/canceled subscriptions with owner/pet hints
export async function fetchAllMemberships(){
  try{
    const res = await axios.get(`${API_BASE_URL}/api/memberships`);
    if(Array.isArray(res.data)) return res.data;
    return [];
  }catch(err){
    console.error('Failed to fetch memberships:', err);
    throw err;
  }
}

// list available plans (id, name, price)
export async function fetchMembershipPlans(){
  try{
    const res = await axios.get(`${API_BASE_URL}/api/memberships/plans`);
    if(Array.isArray(res.data)) return res.data;
    return [];
  }catch(err){
    console.error('Failed to fetch plans:', err);
    throw err;
  }
}

// create (subscribe) user to a plan
export async function createMembershipForUser(userId, payload){
  try{
    const res = await axios.post(`${API_BASE_URL}/api/memberships/user/${userId}`, payload || {});
    return res.data;
  }catch(err){
    console.error('Create membership failed:', err);
    throw err;
  }
}

// preview proration when changing plan
export async function previewMembershipProration(membershipId, newPlanId){
  try{
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${membershipId}/preview-proration`, { newPlanId });
    return res.data; // { immediateCharge, nextInvoiceAdjustment }
  }catch(err){
    console.error('Proration preview failed:', err);
    throw err;
  }
}

// change plan
export async function changeMembershipPlan(membershipId, payload){
  try{
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${membershipId}/change-plan`, payload || {});
    return res.data;
  }catch(err){
    console.error('Change plan failed:', err);
    throw err;
  }
}

// pause / resume / cancel
export async function pauseMembership(membershipId){
  try{
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${membershipId}/pause`);
    return res.data;
  }catch(err){
    console.error('Pause failed:', err);
    throw err;
  }
}

export async function resumeMembership(membershipId){
  try{
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${membershipId}/resume`);
    return res.data;
  }catch(err){
    console.error('Resume failed:', err);
    throw err;
  }
}

export async function cancelMembership(membershipId, payload){
  try{
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${membershipId}/cancel`, payload || {});
    return res.data;
  }catch(err){
    console.error('Cancel failed:', err);
    throw err;
  }
}

// resend receipt
export async function resendMembershipReceipt(membershipId){
  try{
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${membershipId}/resend-receipt`);
    return res.data;
  }catch(err){
    console.error('Resend receipt failed:', err);
    throw err;
  }
}

// Returns true if current user can edit people/pets
export function canEdit(currentUser) {
  if (!currentUser || !currentUser.info || !currentUser.info.job) return false;

  const role = String(currentUser.info.job).toLowerCase();
  return role === 'admin' || role === 'manager' || role === 'reception';
}


// Club Membership
export async function cancelClubMembership(membershipId) {
  var url = API_BASE_URL + '/api/club-memberships/' + membershipId + '/cancel';

  try {
    var response = await axios.post(url);
    var data = response.data;
    return data;
  } catch (error) {
    var message = 'Failed to cancel club membership';

    if (error && error.response && error.response.data && error.response.data.message) {
      message = error.response.data.message;
    } else if (error && error.message) {
      message = error.message;
    }

    console.error('❌ ' + message);
    throw new Error(message);
  }
}

// ------------------------------
// Community API
// ------------------------------

export const fetchCommunities = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/communities`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch communities:', error);
    throw error;
  }
};

export const fetchCommunityById = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/communities/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch community:', error);
    throw error;
  }
};

export const createCommunity = async (payload) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/communities`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create community:', error);
    throw error;
  }
};

export const updateCommunity = async (id, payload) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/communities/${id}`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update community:', error);
    throw error;
  }
};

export const toggleCommunityActiveStatus = async (id, isActive) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/api/communities/${id}/active`, {
      isActive
    });
    return response.data;
  } catch (error) {
    console.error('Failed to toggle community active status:', error);
    throw error;
  }
};

// ------------------------------
// User Community API
// ------------------------------

export const linkUserToCommunity = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/user-community`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to link user to community:', error);
    throw error;
  }
};

export const fetchAllUserCommunities = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/user-community`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user-community list:', error);
    throw error;
  }
};

export const fetchUserCommunityById = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/user-community/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user-community record:', error);
    throw error;
  }
};

export const updateUserCommunityRecord = async (id, payload) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/user-community/${id}`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to update user-community record:', error);
    throw error;
  }
};

export const verifyUserCommunity = async (id) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/user-community/${id}/verify`);
    return response.data;
  } catch (error) {
    console.error('Failed to verify user community:', error);
    throw error;
  }
};
